export function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TIMER_TASK_COLORS: Record<string, string> = {
  'On Call': '#42A5F5',
  Implementing: '#AB47BC',
  Documenting: '#26A69A',
  Support: '#FFA726',
  Rocks: '#f76100',
  'Misc Todos': '#78909C',
  'Internal Implementation': '#5C6BC0',
  Accounting: '#66BB6A',
  EA: '#EC407A',
  HR: '#7E57C2',
  'Sales/Marketing': '#26C6DA',
};

export function getTimerTaskColor(name: string): string {
  return TIMER_TASK_COLORS[name] || '#90A4AE';
}

const TIMER_TASK_ABBREV: Record<string, string> = {
  'On Call': 'OC',
  Implementing: 'IMP',
  Documenting: 'DOC',
  Support: 'SUP',
  Rocks: 'RCK',
  'Misc Todos': 'MISC',
  'Internal Implementation': 'INT',
  Accounting: 'ACC',
  EA: 'EA',
  HR: 'HR',
  'Sales/Marketing': 'S/M',
};

export function timerTaskAbbrev(name: string): string {
  return TIMER_TASK_ABBREV[name] || name.slice(0, 3).toUpperCase();
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Backend timestamps from SQLite's `datetime('now')` come back as
// 'YYYY-MM-DD HH:MM:SS' with NO timezone marker. JavaScript's Date
// constructor interprets that as LOCAL time, which shifts the displayed
// value by the user's UTC offset (e.g., a call at 9:20 AM CT, stored as
// 14:20:00 UTC, would render as "2:20 PM" in CT). These helpers append a
// 'Z' marker before parsing so the timestamp is read as UTC, then
// toLocaleString() converts to the user's local timezone for display.
//
// ISO 8601 timestamps that already carry Z or ±HH:MM are passed through
// unchanged. JS-side `new Date().toISOString()` output also passes through.
export function parseBackendDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  const normalized = hasTz
    ? s
    : (s.includes('T') ? s + 'Z' : s.replace(' ', 'T') + 'Z');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatBackendDateTime(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
): string {
  const d = parseBackendDate(value);
  return d ? d.toLocaleString([], options) : '';
}
